import { useAppSelector } from 'reduxTyped'
import { FREE_TOUCHED_CONTACTS_LIMIT } from 'common/billing/constants'
import { getInboxData } from 'common/billing/entities/addOns/selectors'
import { ProductPrice } from 'common/billing/entities/pro/interfaces'
import { getActiveProduct } from 'common/billing/entities/pro/selectors'
import {
  getSubscriptionData,
  SubscriptionData,
} from 'common/billing/entities/subscription/selectors/subscriptionData'
import { Ability } from 'common/core/constants/Ability'
import { useAbilities } from 'common/core/hooks/useAbilities'

import { AddOnSlug } from '../entities/addOns/interfaces'

// Field names below are intentionally snake_case, not camelCase — they mirror the
// Pricing Phase 5 interface-contracts doc verbatim so Billing/Growth consumers can
// match this shape 1:1 against the contract. Deliberate deviation from
// `docs/naming.md`'s camelCase convention, not an oversight.
export interface CurrentSubscriptionPlanConditions {
  activeContactsLimit: number
  usersLimit: number
  channelsCount: number
  ai: boolean
}

export interface CurrentSubscription extends SubscriptionData {
  conditions: CurrentSubscriptionPlanConditions
  /** Price for all currently active Inbox (livechat) seats. */
  livechatSeatsPrice: ProductPrice
}

/**
 * Composes the account's current subscription data — plan/add-on line items,
 * billing period, next payment date, livechat seats price, and contacts/user/
 * channel limits — into the single shape defined by the Pricing Phase 5
 * interface contracts, so Pro Migration UI (Success/Migration screens, top
 * banner) and Growth's forced-migration modal don't each re-derive it from
 * `getSubscriptionData` / `getActiveProduct` / `getInboxData` / abilities
 * themselves.
 *
 * Pure composition hook — reads already-fetched state, dispatches no fetch of
 * its own. Callers that need fresh data should ensure `fetchPro()` has been
 * dispatched (e.g. via `useBilling`) before/while using this hook.
 *
 * `getInboxData` (livechat seats) is composed here at the hook level rather
 * than folded into `getSubscriptionData` — keeps that selector scoped to plan/
 * add-on billing line items, and mirrors how `useAbilities` is already
 * composed here rather than inside a selector.
 */
export const useCurrentSubscription = (): Nullable<CurrentSubscription> => {
  const activeProduct = useAppSelector(getActiveProduct)
  const [usersTotalAbility, channelsCountAbility] = useAbilities([
    Ability.USERS_TOTAL,
    Ability.CHANNELS_COUNT,
  ] as const)
  const subscriptionData = useAppSelector(getSubscriptionData)
  const inboxData = useAppSelector(getInboxData)

  const activeContactsLimit =
    activeProduct && 'limit_touched_contacts' in activeProduct
      ? activeProduct.limit_touched_contacts
      : FREE_TOUCHED_CONTACTS_LIMIT

  if (!subscriptionData) {
    return null
  }

  return {
    ...subscriptionData,
    livechatSeatsPrice: inboxData.price,
    conditions: {
      activeContactsLimit: activeContactsLimit,
      usersLimit: usersTotalAbility.limit,
      channelsCount: channelsCountAbility.limit,
      ai: subscriptionData.addOns.some((addOn) => addOn.slug === AddOnSlug.AI),
    },
  }
}
